import { Add, Clear, Edit, ExpandLess, ExpandMore, RestartAlt, Save } from '@mui/icons-material';
import { Collapse, Button, List, Typography, Box, Divider, IconButton, ListItem, ListItemText, TextField } from '@mui/material';
import { FC, useState } from 'react';
import { ICustomPageSection, SectionType } from '../../../models';
import { getRandomNegativeNumber } from '../../../helpers';
import { DEFAULT_SECTION } from './ManageCustomPage';
import { AccordionSubContainer } from './AccordionSubContainer';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

interface IAccordionSection {
  customPageSection: ICustomPageSection[];
  setCustomPageSection: (val: ICustomPageSection[]) => void;
  isEditing: {
    [id: string]: boolean;
  };
  setEditing: (val: { [id: number]: boolean }) => void;
  setSections: (val: ICustomPageSection[]) => void;
  subSection: ICustomPageSection;
  parentSections: ICustomPageSection[];
  parentIndex: number;
  selectedType: string;
  index: number;
}

export const AccordionSection: FC<IAccordionSection> = ({
  index,
  selectedType,
  isEditing,
  setEditing,
  customPageSection,
  setSections,
  subSection,
  parentSections,
  parentIndex
}) => {
  const [isAccordionExpanded, setAccordionExpanded] = useState<{
    [item: string]: boolean;
  }>({});
  const classes = useStyles();
  const copy = JSON.parse(JSON.stringify(parentSections));
  const isInEditMode = isEditing[subSection.customPageSectionId];
  const currentItem = copy[parentIndex].sections[index];
  return (
    <div>
      <Typography variant="h6">Accordion Section</Typography>
      <Typography variant="caption">Add accordion section names and sub links with headings and buttons</Typography>
      <List>
        {isInEditMode && <Divider sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />}
        <ListItem sx={{ padding: `4px 0` }}>
          {isInEditMode ? (
            <>
              <Box
                sx={{
                  margin: '0.5rem 0',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  marginRight: '1rem'
                }}
              >
                <TextField
                  size="small"
                  label="Name"
                  autoComplete="nope"
                  variant="outlined"
                  value={subSection.name}
                  required
                  fullWidth
                  sx={{ marginBottom: '0.75rem' }}
                  onChange={e => {
                    currentItem.name = e.target.value;
                    setSections(copy);
                  }}
                  error={isInEditMode && !currentItem.name ? true : false}
                  helperText={isInEditMode && !currentItem.name && 'Required'}
                />
              </Box>
            </>
          ) : (
            <>
              <ListItemText className={classes.listItemText}>
                <div>
                  <Typography>{subSection.name}</Typography>
                </div>
                {subSection.sections && subSection.sections.length > 0 && !isInEditMode && (
                  <IconButton
                    sx={{ padding: '2px' }}
                    onClick={() => {
                      setAccordionExpanded({
                        ...isAccordionExpanded,
                        [subSection.name]: !isAccordionExpanded[subSection.name]
                      });
                    }}
                    aria-expanded={isAccordionExpanded[subSection.name]}
                    aria-label="show more nav items"
                  >
                    {isAccordionExpanded[subSection.name] ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
              </ListItemText>
            </>
          )}

          <Box display="flex" flexDirection={isInEditMode ? 'column' : 'row'} alignSelf={isInEditMode ? 'flex-start' : 'auto'}>
            <IconButton
              onClick={e => {
                setEditing({
                  [subSection.customPageSectionId]: !isInEditMode
                });
              }}
              sx={{ p: '2px' }}
              title={isInEditMode ? 'Save' : 'Edit'}
              disabled={isInEditMode && !subSection.name}
              color={isInEditMode ? 'primary' : 'inherit'}
            >
              {isInEditMode ? <Save /> : <Edit />}
            </IconButton>
            {isInEditMode && (
              <IconButton
                title="Reset"
                sx={{ p: '2px' }}
                color="secondary"
                onClick={e => {
                  setSections(parentSections);
                }}
              >
                <RestartAlt />
              </IconButton>
            )}
            <IconButton
              color="error"
              title="Remove"
              sx={{ p: '2px' }}
              onClick={() => {
                const result = window.confirm('Are you sure you want to remove this item?');
                if (result) {
                  const newItems = currentItem.sections.filter(sec => sec.customPageSectionId !== subSection.customPageSectionId);
                  copy[parentIndex].sections[index].sections = newItems;
                  setSections(copy);
                }
              }}
            >
              <Clear />
            </IconButton>
          </Box>
        </ListItem>
        {isInEditMode && <Divider />}
        {subSection.sections.map((item, subSectionIndex) => {
          return (
            <Collapse key={`${subSectionIndex}`} in={isAccordionExpanded[subSection.name]} timeout="auto" unmountOnExit>
              <AccordionSubContainer
                isEditing={isEditing}
                setEditing={setEditing}
                item={item}
                subLinks={item.sections}
                setAccordionExpanded={setAccordionExpanded}
                isAccordionExpanded={isAccordionExpanded}
                setSections={setSections}
                sections={parentSections}
                grandParentIndex={parentIndex}
                parentIndex={index}
                index={subSectionIndex}
              />
            </Collapse>
          );
        })}
        <Button
          color="secondary"
          startIcon={<Add />}
          sx={{ fontSize: '12px', padding: '0', marginLeft: '1rem' }}
          onClick={() => {
            const randomNumber = getRandomNegativeNumber();
            currentItem.sections.push({
              ...DEFAULT_SECTION,
              name: 'Link Title',
              // Negative numbers will still create a new record, but are distinct so we can use them on the frontend before the user saves
              customPageSectionId: randomNumber,
              type: SectionType.BUTTON_LINK,
              sortOrder: currentItem.sections.length + 1
            });
            setEditing({
              [randomNumber]: true
            });
            setAccordionExpanded({
              ...isAccordionExpanded,
              [subSection.name]: true
            });
            setSections(copy);
          }}
        >
          Add Link Item
        </Button>
      </List>
    </div>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  listItemText: {
    '& span': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  externalLink: {
    wordBreak: 'break-word'
  }
}));
